import { Time } from "@iluvatar/global/src/utilities/time";
import { useQuery } from "@tanstack/react-query";
import { getBlob, getDownloadURL, ref as stgRef } from "firebase/storage";

import { parseUserStoragePath } from "../services/file/parse-user-storage-path";
import { storage } from "../services/firebase.storage";
import requestService from "../services/request.service";
import { useObjectUrl } from "./useObjectUrl";

const IMAGE_CACHE_TIME = 2 * Time.days;

export function useStorageUrl(
  ref?: string,
  isPrivate = false,
  communityId?: string,
): [string | undefined, boolean, (Error & { code?: string }) | null] {
  const objectPath = ref?.replace(/^gs:\/\/[^/]+\//, "").replace(/^\//, "");
  const isImageBlob =
    isPrivate ||
    Boolean(objectPath && /^(users|conversation)\//.test(objectPath));
  const { data, isLoading, error } = useQuery<
    string | Blob,
    Error & { code?: string }
  >({
    queryKey: ["storage-url", ref, isPrivate, communityId],
    enabled: Boolean(ref),
    staleTime: IMAGE_CACHE_TIME,
    gcTime: IMAGE_CACHE_TIME,
    refetchOnMount: (args) => args.isStale(),
    refetchOnWindowFocus: (args) => args.isStale(),
    refetchOnReconnect: (args) => args.isStale(),
    async queryFn() {
      if (isPrivate) {
        if (!communityId?.trim()) {
          throw new Error("A communityId is required for private images.");
        }
        const { userId, fileName } = parseUserStoragePath(ref);
        const imageApiPath = `/api/images/${userId}`;
        const query = `?fileName=${encodeURIComponent(fileName)}&communityId=${communityId}`;
        const [, { data: imageAccess }] = await requestService.request<{
          data: { url: string; expiresAt: number };
        }>(`${imageApiPath}/url${query}`);

        // Download immediately so the cache contains bytes, not an expiring URL.
        const response = await fetch(imageAccess.url);
        if (!response.ok) {
          throw new Error("Unable to load private image.");
        }
        return response.blob();
      }

      const file = stgRef(storage, ref);
      return isImageBlob ? getBlob(file) : getDownloadURL(file);
    },
  });

  const blobUrl = useObjectUrl(data instanceof Blob ? data : undefined);
  return [typeof data === "string" ? data : blobUrl, isLoading, error];
}
